import { useMemo, useRef, useState } from 'react';
import { FormFieldInputType } from 'models/enum';
import { ChartMetaFormField, ContentItem } from 'models/ui';
import { ELSButton, ELSIcon } from 'components/common/els';
import { CollectionColumn, CollectionConfig, OTHER_OPTION_VALUE, SPECIFIC_ALLERGENS_BY_TYPE } from './constants';
import { useDismissOnOutsideClick } from './AnnualAmbulatoryCareSelect';

export type ModalRow = Record<string, string>;

export interface AnnualAmbulatoryCareAddModalProps {
  config: CollectionConfig;
  formFieldMap: Map<string, ChartMetaFormField>;
  initialRows: ModalRow[];
  onCommit: (rows: ModalRow[]) => void;
  onClose: VoidFunction;
}

interface OptionItem {
  value: string;
  label: string;
  parentValue?: string;
}

const MULTI_VALUE_SEPARATOR = ', ';

const splitMulti = (value: string): string[] => (value ? value.split(MULTI_VALUE_SEPARATOR).filter(Boolean) : []);

const includesOther = (column: CollectionColumn, value: string): boolean => {
  if (column.type === FormFieldInputType.MULTISELECT_DROPDOWN) return splitMulti(value).includes(OTHER_OPTION_VALUE);
  return value === OTHER_OPTION_VALUE;
};

interface ModalMultiSelectProps {
  options: OptionItem[];
  selected: string[];
  ariaLabel: string;
  onToggle: (value: string) => void;
  onClear?: VoidFunction;
  disabled?: boolean;
}

export const ModalMultiSelect = ({ options, selected, ariaLabel, onToggle, onClear, disabled }: ModalMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useDismissOnOutsideClick(containerRef, open, () => setOpen(false));

  const handleClear = () => {
    if (onClear) onClear();
    setOpen(false);
  };

  return (
    <div className="annual-ambulatory-care__multiselect c-els-field" ref={containerRef}>
      <span className="c-els-field__wrap c-els-field__wrap--icon-right">
        <ELSIcon prefix="hmds" name="chevron-down" size="1x1o2" customClass="c-els-field__icon c-els-field__icon--right" />
        <button
          type="button"
          className="c-els-field__input annual-ambulatory-care__multiselect-trigger"
          aria-label={ariaLabel}
          aria-haspopup="true"
          aria-expanded={open}
          disabled={disabled}
          onClick={() => setOpen((prev) => !prev)}
        >
          {selected.length ? selected.join(', ') : <span className="annual-ambulatory-care__select-placeholder">Select</span>}
        </button>
      </span>
      {open && !disabled && (
        <div className="annual-ambulatory-care__multiselect-panel" role="group" aria-label={ariaLabel}>
          <button type="button" className="annual-ambulatory-care__select-clear" onClick={handleClear}>
            Clear
          </button>
          {options.map((option) => {
            const optionId = `ms-${ariaLabel}-${option.value}`.replace(/\s+/g, '-');
            return (
              <label className="annual-ambulatory-care__checkbox" key={option.value} htmlFor={optionId}>
                <input id={optionId} type="checkbox" checked={selected.includes(option.value)} onChange={() => onToggle(option.value)} />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface ModalSelectProps {
  options: OptionItem[];
  value: string;
  ariaLabel: string;
  onChange: (value: string) => void;
}

export const ModalSelect = ({ options, value, ariaLabel, onChange }: ModalSelectProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useDismissOnOutsideClick(containerRef, open, () => setOpen(false));

  const selectedLabel = options.find((option) => option.value === value)?.label;

  const handleSelect = (nextValue: string) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <div className="annual-ambulatory-care__select c-els-field" ref={containerRef}>
      <span className="c-els-field__wrap c-els-field__wrap--icon-right">
        <ELSIcon prefix="hmds" name="chevron-down" size="1x1o2" customClass="c-els-field__icon c-els-field__icon--right" />
        <button
          type="button"
          className="c-els-field__input annual-ambulatory-care__select-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className={selectedLabel ? undefined : 'annual-ambulatory-care__select-placeholder'}>{selectedLabel ?? 'Select'}</span>
        </button>
      </span>
      {open && (
        <div className="annual-ambulatory-care__select-panel" role="listbox" aria-label={ariaLabel}>
          <button type="button" role="option" aria-selected={value === ''} className="annual-ambulatory-care__select-clear" onClick={() => handleSelect('')}>
            Clear
          </button>
          {options.map((option) => (
            <button
              type="button"
              role="option"
              aria-selected={option.value === value}
              key={option.value}
              className="annual-ambulatory-care__select-option"
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
ModalSelect.displayName = 'ModalSelect';

const AnnualAmbulatoryCareAddModal = ({ config, formFieldMap, initialRows, onCommit, onClose }: AnnualAmbulatoryCareAddModalProps) => {
  const [rows, setRows] = useState<ModalRow[]>(initialRows);
  const [draft, setDraft] = useState<ModalRow>({});

  const getOptions = useMemo(
    () =>
      (field: string, parentField?: string, parentSelectedValue?: string): OptionItem[] => {
        const content = (formFieldMap.get(field)?.chartContent ?? []) as ContentItem[];
        const options = content
          .filter((item) => item.value != null && item.value !== '')
          .map((item) => ({ value: String(item.value), label: item.label ?? String(item.value), parentValue: item.parentId }));

        if (!parentField) return options;
        if (!parentSelectedValue) return [];

        if (parentSelectedValue === OTHER_OPTION_VALUE) return [{ value: OTHER_OPTION_VALUE, label: OTHER_OPTION_VALUE }];

        const byParentId = options.filter((opt) => Boolean(opt.parentValue) && opt.parentValue === parentSelectedValue);
        if (byParentId.length > 0) return byParentId;

        const allowedLabels = SPECIFIC_ALLERGENS_BY_TYPE[parentSelectedValue];
        if (allowedLabels) {
          const allowedSet = new Set(allowedLabels.map((s) => s.toLowerCase()));
          return options.filter((opt) => allowedSet.has(opt.label.toLowerCase()));
        }

        return [];
      },
    [formFieldMap]
  );

  const setDraftValue = (field: string, value: string) => setDraft((prev) => ({ ...prev, [field]: value }));

  const handleColumnChange = (field: string, value: string) => {
    setDraft((prev) => {
      const next: ModalRow = { ...prev, [field]: value };
      config.columns.forEach((col) => {
        if (col.dependsOn === field) {
          next[col.field] = '';
          if (col.otherField) next[col.otherField] = '';
        }
      });
      return next;
    });
  };

  const toggleMultiValue = (field: string, optionValue: string) => {
    setDraft((prev) => {
      const current = splitMulti(prev[field] ?? '');
      const next = current.includes(optionValue) ? current.filter((v) => v !== optionValue) : [...current, optionValue];
      return { ...prev, [field]: next.join(MULTI_VALUE_SEPARATOR) };
    });
  };

  const trimmedDraftValue = (field?: string): string => (field ? String(draft[field] ?? '').trim() : '');

  const isDraftDirty = config.columns.some((column) => Boolean(draft[column.field]) || (column.otherField && Boolean(draft[column.otherField])));

  const hasContent = config.columns.some((column) => Boolean(trimmedDraftValue(column.field)) || Boolean(trimmedDraftValue(column.otherField)));

  const hasDescribedOthers = config.columns.every((column) => {
    const value = trimmedDraftValue(column.field);
    if (!column.otherField || !includesOther(column, value)) return true;
    return Boolean(trimmedDraftValue(column.otherField));
  });

  const canAddDraft = hasContent && hasDescribedOthers;

  const handleAddDraft = () => {
    if (!canAddDraft) return;
    setRows((prev) => [...prev, draft]);
    setDraft({});
  };

  const handleResetDraft = () => setDraft({});

  const handleDeleteRow = (rowIndex: number) => setRows((prev) => prev.filter((_, index) => index !== rowIndex));

  const handleSave = () => {
    onCommit(rows);
    onClose();
  };

  const formatCell = (row: ModalRow, column: CollectionColumn): string => {
    const base = String(row[column.field] ?? '').trim();
    const other = column.otherField ? String(row[column.otherField] ?? '').trim() : '';
    return other ? `${base}: ${other}` : base;
  };

  const renderControl = (column: CollectionColumn) => {
    const value = draft[column.field] ?? '';
    const parentSelectedValue = column.dependsOn ? draft[column.dependsOn] ?? '' : undefined;
    const isDisabled = column.disabledWhen ? draft[column.disabledWhen.field] === column.disabledWhen.value : false;

    if (column.type === FormFieldInputType.DROPDOWN) {
      return (
        <ModalSelect
          options={getOptions(column.field, column.dependsOn, parentSelectedValue)}
          value={value}
          ariaLabel={column.header}
          onChange={(selectedValue) => handleColumnChange(column.field, selectedValue)}
        />
      );
    }

    if (column.type === FormFieldInputType.MULTISELECT_DROPDOWN) {
      const handleClearMulti = () => {
        setDraft((prev) => {
          const next = { ...prev, [column.field]: '' };
          if (column.otherField) next[column.otherField] = '';
          return next;
        });
      };
      return (
        <ModalMultiSelect
          options={getOptions(column.field, column.dependsOn, parentSelectedValue)}
          selected={splitMulti(value)}
          ariaLabel={column.header}
          onToggle={(optionValue) => toggleMultiValue(column.field, optionValue)}
          onClear={handleClearMulti}
          disabled={isDisabled}
        />
      );
    }

    return (
      <input
        className="c-els-field__input"
        type="text"
        value={value}
        maxLength={150}
        onChange={(event) => setDraftValue(column.field, event.target.value)}
        aria-label={column.header}
      />
    );
  };

  return (
    <div className="annual-ambulatory-care__modal-backdrop" role="dialog" aria-modal="true" aria-label={config.modalTitle}>
      <div className="annual-ambulatory-care__modal">
        <div className="annual-ambulatory-care__modal-header">
          <h3>{config.modalTitle}</h3>
          <button className="annual-ambulatory-care__modal-close" type="button" onClick={onClose} aria-label="Close">
            <ELSIcon name="close" size="1x" />
          </button>
        </div>

        <div className="annual-ambulatory-care__modal-form">
          <div className={`annual-ambulatory-care__modal-fields annual-ambulatory-care__modal-fields--${config.id}`}>
            {config.columns.map((column) => {
              const showOther = column.otherField && includesOther(column, draft[column.field] ?? '');
              return (
                <div className="annual-ambulatory-care__modal-field" key={column.field}>
                  <span className="c-els-field__label">{column.header}</span>
                  {renderControl(column)}
                  {showOther && column.otherField && (
                    <input
                      className="c-els-field__input annual-ambulatory-care__modal-other"
                      type="text"
                      value={draft[column.otherField] ?? ''}
                      maxLength={150}
                      placeholder="Other"
                      aria-label={`${column.header} other`}
                      onChange={(event) => setDraftValue(column.otherField as string, event.target.value)}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="annual-ambulatory-care__modal-actions">
            <ELSButton text="Add" isDisabled={!canAddDraft} onClick={handleAddDraft} />
            <ELSButton text="Reset" type="secondary" isDisabled={!isDraftDirty} onClick={handleResetDraft} />
          </div>

          {rows.length > 0 && (
            <>
              <div className="annual-ambulatory-care__modal-added">
                <h4>{`Added ${config.title}`}</h4>
                <table className={`annual-ambulatory-care__table annual-ambulatory-care__table--${config.id} annual-ambulatory-care__table--has-actions`}>
                  <thead>
                    <tr>
                      {config.columns.map((column) => (
                        <th key={column.field}>{column.header}</th>
                      ))}
                      <th className="annual-ambulatory-care__row-actions" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, rowIndex) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <tr key={rowIndex}>
                        {config.columns.map((column) => (
                          <td key={column.field}>{formatCell(row, column)}</td>
                        ))}
                        <td className="annual-ambulatory-care__row-actions">
                          <button className="annual-ambulatory-care__row-delete" type="button" onClick={() => handleDeleteRow(rowIndex)} aria-label="Delete row">
                            <ELSIcon name="trash" prefix="gizmo" color="secondary" size="1x" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="annual-ambulatory-care__modal-footer">
                <ELSButton text="Save" onClick={handleSave} />
                <ELSButton text="Cancel" type="secondary" onClick={onClose} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

AnnualAmbulatoryCareAddModal.displayName = 'AnnualAmbulatoryCareAddModal';
export default AnnualAmbulatoryCareAddModal;
